import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paper, useMediaQuery, useTheme } from '@mui/material';
import { HOVER_PREVIEW_VIEWPORT_INSET_PX } from '../common/hoverPreviewBoardSizes';
import { useHoverPreviewBoardWidth } from '../common/useHoverPreviewBoardWidth';
import ReplayGamePreviewBoard from './ReplayGamePreviewBoard';

const HOVER_ENTER_DELAY_MS = 350;
const HOVER_LEAVE_DELAY_MS = 120;
const VIEWPORT_RIGHT_INSET_PX = HOVER_PREVIEW_VIEWPORT_INSET_PX;
const VIEWPORT_TOP_MIN_PX = 8;

type GameHoverPreviewProps = {
  gameId: string;
  openingSans?: string[];
  startFen?: string;
  children: React.ReactElement;
};

const clampPanelTop = (anchorTop: number, boardWidth: number) => {
  const maxTop = Math.max(
    VIEWPORT_TOP_MIN_PX,
    window.innerHeight - boardWidth - VIEWPORT_TOP_MIN_PX,
  );
  return Math.min(Math.max(VIEWPORT_TOP_MIN_PX, anchorTop), maxTop);
};

const GameHoverPreview = ({
  gameId,
  openingSans,
  startFen,
  children,
}: GameHoverPreviewProps) => {
  const theme = useTheme();
  const hoverCapable = useMediaQuery('(hover: hover) and (pointer: fine)');
  const hoverBoardWidth = useHoverPreviewBoardWidth();
  const anchorRef = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [panelTop, setPanelTop] = useState(VIEWPORT_TOP_MIN_PX);
  const enterTimerRef = useRef(0);
  const leaveTimerRef = useRef(0);

  const clearTimers = useCallback(() => {
    window.clearTimeout(enterTimerRef.current);
    window.clearTimeout(leaveTimerRef.current);
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const syncPanelTop = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    setPanelTop(clampPanelTop(rect.top, hoverBoardWidth));
  }, [hoverBoardWidth]);

  useEffect(() => {
    if (!open) {
      return;
    }

    syncPanelTop();
    window.addEventListener('scroll', syncPanelTop, true);
    window.addEventListener('resize', syncPanelTop);
    return () => {
      window.removeEventListener('scroll', syncPanelTop, true);
      window.removeEventListener('resize', syncPanelTop);
    };
  }, [open, syncPanelTop]);

  const scheduleOpen = useCallback(() => {
    window.clearTimeout(leaveTimerRef.current);
    window.clearTimeout(enterTimerRef.current);
    enterTimerRef.current = window.setTimeout(() => {
      syncPanelTop();
      setOpen(true);
    }, HOVER_ENTER_DELAY_MS);
  }, [syncPanelTop]);

  const scheduleClose = useCallback(() => {
    window.clearTimeout(enterTimerRef.current);
    window.clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = window.setTimeout(() => {
      setOpen(false);
    }, HOVER_LEAVE_DELAY_MS);
  }, []);

  const setAnchorRef = useCallback(
    (node: HTMLElement | null) => {
      anchorRef.current = node;
      const childRef = (children as React.ReactElement & { ref?: React.Ref<HTMLElement> })
        .ref;
      if (typeof childRef === 'function') {
        childRef(node);
      } else if (childRef && typeof childRef === 'object') {
        (childRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    [children],
  );

  const child = React.cloneElement(children, {
    ref: setAnchorRef,
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
      if (hoverCapable) {
        scheduleOpen();
      }
      children.props.onMouseEnter?.(event);
    },
    onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
      scheduleClose();
      children.props.onMouseLeave?.(event);
    },
  });

  return (
    <>
      {child}
      {hoverCapable && open ? (
        <Paper
          elevation={8}
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
          sx={{
            position: 'fixed',
            right: VIEWPORT_RIGHT_INSET_PX,
            top: panelTop,
            zIndex: theme.zIndex.tooltip,
            p: 0.5,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        >
          <ReplayGamePreviewBoard
            key={gameId}
            gameId={gameId}
            openingSans={openingSans}
            startFen={startFen}
            boardWidth={hoverBoardWidth}
          />
        </Paper>
      ) : null}
    </>
  );
};

export default GameHoverPreview;
